import { FC, useState } from 'react';
import styled from '@emotion/styled';
import { ISurveyQuestion, SURVEY_QUESTION, NPS_SCORES } from 'domain/entities/Survey/Survey';
import { FieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';

const Container = styled.div`
    padding-bottom: 20px;
`;

const Question = styled.div`
    font-size: 16px;
    padding-bottom: 8px;
    font-weight: var(--font-bold);
    display: flex;
    align-items: center;

    span {
        font-size: 9px;
        color: ${(props: { required: boolean }) => (props.required ? ' var(--white)' : '#999')};
        background-color: ${(props: { required: boolean }) => (props.required ? ' #ff4500' : '#E8E8E8')};
        text-align: center;
        border-radius: 3px;
        font-weight: 700;
        padding: 3px;
    }
`;

const Description = styled.div`
    font-size: 9px;
    border-top: 1px dotted #ccc;
    border-bottom: 1px dotted #ccc;
    padding: 5px 0;
    margin: 5px 0;
`;

const NpsContainer = styled.div`
    padding: 10px 10px 0 10px;
    display: flex;
`;

const NpsItem = styled.div`
    width: 9%;
    font-size: 13px;
    padding: 5px;
    box-sizing: border-box;
    border-radius: 30px;
    border: 1px solid var(--main-color-blue);
    text-align: center;
    margin: 0 0.3%;
    cursor: pointer;
    display: block;
    background-color: ${(props: { selected: boolean }) => (props.selected ? 'var(--main-color-blue)' : 'transparent')};
    color: ${(props: { selected: boolean }) => (props.selected ? 'var(--white)' : 'var(--black)')};
    transition: all 0.3s;
    font-weight: ${(props: { selected: boolean }) => (props.selected ? '700' : '500')};
`;

const NpsDescription = styled.div`
    display: flex;
    width: 100%;
    font-size: 10px;
    padding: 5px 0;
`;

const NpsDescriptionLow = styled.div`
    width: 50%;
    text-align: left;
`;

const NpsDescriptionHigh = styled.div`
    width: 50%;
    text-align: right;
`;

const FreeTxtContainer = styled.div`
    padding: 8px 10px;
    width: 90%;
    margin: 0 auto;

    textarea {
        min-height: 90px;
        max-height: 300px;
        resize: none;
        line-height: 1.9em;
    }
`;

const AnswerTextLimit = styled.div`
    font-size: 9px;
    text-align: right;
`;

const TextAreaElement = styled.textarea`
    color: rgba(0, 0, 0, 0.87);
    min-height: 57px;
    max-height: 420px;
    width: 100%;
    resize: none;
    padding: 8px 10px;
    border: 1px solid var(--main-color-blue);
    border-radius: 4px;
    font-size: ${(props: { fontSize?: string }) => props.fontSize || 'var(--font-size--large)'};
    &:focus {
        border: 2px solid var(--main-color-blue);
        outline: none;
    }
`;

const ErrorMsg = styled.div`
    margin: 5px 0;
    background-color: #ec1313;
    border-radius: 4px;
    color: var(--white);
    padding: 2px;
`;

interface Props {
    index: number;
    question: ISurveyQuestion;
    errors: FieldErrors<FieldValues>;
    register: UseFormRegister<FieldValues>;
    setValue: (name: string, value: number, options?: { shouldValidate?: boolean }) => void;
}

const REQUIRED_MSG = 'この質問への回答は必須です。';
const MAX_LENGTH_MSG = '文字数の上限を超えています。';

export const SurveyQuestion: FC<Props> = ({ index, question, errors, setValue, register }) => {
    const [npsValue, setNpsValue] = useState<number | null>(null);
    const errMsg = errors[question.id]?.message ? String(errors[question.id]?.message) : '';

    // NPSの場合、ここでformの値を更新
    const onChangeNps = (score: number) => {
        setNpsValue(score);
        setValue(String(question.id), score);
    };

    return (
        <Container>
            <Question required={question.required}>
                {index}.{question.title}&nbsp;
                {question.required && <span>必須</span>}
            </Question>
            {question.description && <Description>{question.description}</Description>}
            {errMsg && <ErrorMsg>{errMsg}</ErrorMsg>}
            {question.question_type === SURVEY_QUESTION.NPS && (
                <>
                    <NpsContainer>
                        {NPS_SCORES.map((score) => (
                            <NpsItem key={score} selected={npsValue === score} onClick={() => onChangeNps(score)}>
                                {score}
                            </NpsItem>
                        ))}
                        <input
                            type="hidden"
                            value={npsValue ?? ''}
                            {...register(String(question.id), {
                                required: { value: question.required, message: REQUIRED_MSG },
                            })}
                        />
                    </NpsContainer>
                    <NpsDescription>
                        <NpsDescriptionLow>←当てはまらない</NpsDescriptionLow>
                        <NpsDescriptionHigh>当てはまる→</NpsDescriptionHigh>
                    </NpsDescription>
                </>
            )}

            {question.question_type === SURVEY_QUESTION.FREE && (
                <FreeTxtContainer>
                    <TextAreaElement
                        rows={3}
                        maxLength={question.answer_text_limit || 2000}
                        {...register(String(question.id), {
                            required: { value: question.required, message: REQUIRED_MSG },
                            maxLength: { value: question.answer_text_limit || 2000, message: MAX_LENGTH_MSG },
                        })}
                    />
                    {question.answer_text_limit && (
                        <AnswerTextLimit>{question.answer_text_limit}文字以内</AnswerTextLimit>
                    )}
                </FreeTxtContainer>
            )}

            {question.question_type === SURVEY_QUESTION.SELECTION && (
                <FreeTxtContainer>
                    <FormSelect
                        {...register(String(question.id), {
                            required: { value: question.required, message: REQUIRED_MSG },
                        })}
                        options={question.selection.map((value) => ({
                            value,
                            label: value,
                        }))}
                    />
                </FreeTxtContainer>
            )}
        </Container>
    );
};
